import React, { useState } from 'react'
import { useAtomValue } from 'jotai'

import { Access } from '../../lib'
import * as state from '../../states'
import {
	TOfferSpendAgg,
	TRevenueSpendByAccount,
	TRevenueSpendByAccountOfferSpend,
	ERoleAccess,
	EOfferType,
	TRevenueSpendByAccountRevenueSpend
} from '../../types'
import { IUiTableConfig, UiTable, UiDateInfo } from '../ui'
import * as helpers from '../../helpers'
import { RevenueSpendByAccountRevenueDetailsModal } from './RevenueSpendByAccountRevenueDetailsModal'
import { renderByRevenue } from './RevenueSpendByAccountListTable'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../../types/lang'

type IRevenueSpendByAccountOfferSpendDetailsTableProps = {
	revenueSpend: TRevenueSpendByAccount | TRevenueSpendByAccountOfferSpend | null;
	items: TOfferSpendAgg[];
	skip: number;
}

const NO_DATA_CELL: React.ReactNode = '-'

export const RevenueSpendByAccountOfferSpendDetailsTable = (
	props: IRevenueSpendByAccountOfferSpendDetailsTableProps
) => {
	const {
		revenueSpend,
		items = [],
		skip = 0
	} = props
	const { t } = useTranslation()
	const profile = useAtomValue(state.profileAtom)
	const $access = new Access(profile ?? null)
	//console.log('----- RevenueSpendByAccountOfferSpendDetailsTable::', { revenueSpend })
	const [viewRevenueSpendDetailsInfo, setViewRevenueSpendDetailsInfo] = useState<TRevenueSpendByAccount | TRevenueSpendByAccountRevenueSpend | null>(null)

	const onViewRevenueSpendDetails = (item: TRevenueSpendByAccount | TRevenueSpendByAccountRevenueSpend, e: React.MouseEvent) => {
		e.preventDefault()
		setViewRevenueSpendDetailsInfo(item)
	}

	const tableConfig: IUiTableConfig<TOfferSpendAgg>[] = [
		{
			key: 'id',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_offer_spend_details_table.column_id)
		},
		{
			key: 'at',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_offer_spend_details_table.column_date),
			rowCellFormatter: ({ value, rowData }) => {
				return (
					<UiDateInfo value={value}/>
				)
			}
		},
		{
			key: 'amount',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_offer_spend_details_table.column_amount),
			rowCellFormatter: ({ value, rowData, rowIndex, cellIndex }) => {
				if (!revenueSpend) {
					return NO_DATA_CELL
				}

				//console.log('rowCellFormatter --- Amount --- ', rowData)
				const total = rowData.amount ?? 0
				const offerId = rowData.offerId

				if (!('registeredAt' in revenueSpend)) {
					const formatValue = helpers.formatAmount(total)
					return <>{helpers.thousandSeparator(formatValue)}</>
				}

				const revenueSpendFiltered: TRevenueSpendByAccount = {
					...revenueSpend,
					revenueSpend: revenueSpend.revenueSpend?.filter(item => offerId === item.offerId) ?? []
				}
				const className = revenueSpendFiltered.revenueSpend.length ? 'text-success' : 'text-danger'
				return renderByRevenue($access.$(ERoleAccess.RevenueSpendByAccountList), revenueSpendFiltered, onViewRevenueSpendDetails)(total, rowIndex, cellIndex, className)
			}
		},
		{
			key: 'percent',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_offer_spend_details_table.column_percent),
			classesToAdd: {
				th: 'text-center',
				td: 'text-center'
			},
			rowCellFormatter: ({ value, rowData, rowsData, rowIndex, cellIndex }) => {
				const formatValue = helpers.formatAmount(rowData.percent * 100)

				return <>{helpers.thousandSeparator(formatValue)}%</>
			}
		},
		{
			key: 'offerId',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_offer_spend_details_table.column_offer),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const offerId = rowData?.offerId ?? null
				const offer = rowData?.offer ?? null
				const name = offer?.name ?? null
				return <>
					<strong>[id: {offerId}]</strong>
					&nbsp;
					{name ?? '?'}
				</>
			}
		},
		{
			key: 'offer.scopeId',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_offer_spend_details_table.column_scope),
			rowCellFormatter: ({ rowData }): React.JSX.Element => {
				const scope = rowData?.scope ?? null
				const scopeId = scope?.id ?? null
				const name = scope?.name ?? null
				return <>
					<strong>[id: {scopeId}]</strong>
					&nbsp;
					{name ?? '?'}
				</>
			}
		},
		{
			key: 'notes',
			title: t(ALL_LANG_KEY.revenue_spend_by_account_offer_spend_details_table.column_notes),
			rowCellFormatter: ({ value, rowData }) => {
				return <>{value ?? ''}</>
			}
		}
	]

	return <>
		<UiTable
			data={items}
			config={tableConfig}
			// TODO: uncomment if necessary
			// classesToAdd={{
			// 	wrapper: 'table-sticky-wrapper table-sticky-wrapper--top-60',
			// }}
		/>

		{
			!!viewRevenueSpendDetailsInfo && !!$access.$(ERoleAccess.RevenueSpendByAccountList) &&
			<RevenueSpendByAccountRevenueDetailsModal
				isFullscreenModal={true}
				offerType={EOfferType.spend}
				revenueSpend={viewRevenueSpendDetailsInfo}
				show={!!viewRevenueSpendDetailsInfo}
				take={'all'}
				onClose={() => {
					setViewRevenueSpendDetailsInfo(null)
					// navigate(href(0).tail)
					//warmUp()
				}}
			/>
		}
	</>
}